/**
 * DeliveryFlowStep8 - Send Mail (Wizard Step 7)
 * 
 * Step 7 in the 7-step dispute wizard flow.
 * Final clean PostGrid review and send.
 * 
 * POSTGRID PDF ARCHITECTURE:
 * - Uses review_pdf_url for "Download Ready Letter" button (user-friendly, styled)
 * - Uses postgrid_pdf_url for actual PostGrid submission (mail-safe, validated)
 * - Never sends review_pdf_url to PostGrid unless it passes validation
 * - Shows validation errors without exposing technical details to user
 * 
 * MAIL CLASS OPTIONS (NEW - May 2026):
 * - Standard Class: standard_class ($0.80 base + $0.10/page)
 * - Certified Mail: certified ($0.80 + $6.69 fee)
 * - Certified Mail with Electronic Return Receipt: certified_return_receipt ($0.80 + $9.51 fee)
 * 
 * Features:
 * - Final clean mailing package review
 * - Download Ready Letter (uses review_pdf_url)
 * - PostGrid quote with page count and cost
 * - Cost in dollars and credits
 * - Send by Mail (generates + validates postgrid_pdf_url)
 * - Mailing defaults: Black & White, Single-sided, addressPlacement=top_first_page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  ChevronRight, Loader2, ArrowLeft, Check, Send, DollarSign,
  CreditCard, Mail, Package, AlertCircle, CheckCircle, FileText, Download,
  Shield, ShieldCheck, Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { DisputeWizardStepper, DeliveryFlowStepper } from '../../components/disputes/DisputeWizardStepper';
import {
  buildMailEnvelopeAttachments,
  buildSystemDefaultDataBreachProof,
  dedupeDataBreachEnvelopes,
  isDataBreachEnvelope,
  isResolvableDataBreachProof,
  normalizeDataBreachProof,
} from '../../utils/disputeEnvelopeAttachments';
import { metro2Service } from '../../services/metro2Service';
import {
  buildWizardPacketAttachments,
  persistResolvedPacketAttachments,
  syncWizardPackagePacketAttachments,
} from '../../utils/packetAttachmentPersistence';
import { buildDisputeLetterPdfFilename } from '../../utils/disputeLetterDownloadFilename';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// ============================================
// MAIL CLASS CONFIGURATION (New - May 2026)
// ============================================
// IMPORTANT: Do NOT show assumed delivery days (3-5, 5-7)
// Only show what PostGrid actually provides
const MAIL_CLASS_OPTIONS = [
  {
    value: 'standard',
    label: 'Standard Class',
    description: 'Standard mail delivery',  // No assumed days
    icon: Mail,
    basePrice: 0.80,
    estimatedDelivery: null  // Will be provided by PostGrid
  },
  {
    value: 'certified',
    label: 'Certified Mail',
    description: 'Certified tracking and delivery record',  // No assumed days
    icon: Shield,
    basePrice: 7.49, // $0.80 + $6.69
    estimatedDelivery: null,
    includesTracking: true
  },
  {
    value: 'certified_err',
    label: 'Certified Mail with Electronic Return Receipt',
    description: 'Certified tracking plus electronic return receipt',
    icon: ShieldCheck,
    basePrice: 10.31,
    estimatedDelivery: null,
    includesTracking: true,
    includesSignature: true
  }
];

// ============================================
// PRICE & CREDITS REVIEW COMPONENT (Updated)
// ============================================
const PriceCreditsReview = ({ quote, loading, mailClass, onMailClassChange, envelopes, packageId }) => {
  const [mailClasses, setMailClasses] = useState(MAIL_CLASS_OPTIONS);
  
  // Fetch mail classes from backend if needed
  useEffect(() => {
    const fetchMailClasses = async () => {
      try {
        const response = await axios.get(`${API}/delivery/mail/classes`);
        if (response.data?.ok && response.data.mail_classes) {
          // Use backend data if available, otherwise fallback to static config
          const backendClasses = response.data.mail_classes.map(mc => ({
            value: mc.value,
            label: mc.label,
            description: mc.description,
            icon: mc.value === 'standard' ? Mail : mc.value === 'certified' ? Shield : ShieldCheck,
            basePrice: mc.base_price_cents / 100,
            estimatedDelivery: mc.estimated_delivery,
            includesTracking: mc.includes_tracking,
            includesSignature: mc.includes_signature
          }));
          if (backendClasses.length > 0) {
            setMailClasses(backendClasses);
          }
        }
      } catch (err) {
        console.log('Using default mail class config');
      }
    };
    fetchMailClasses();
  }, []);
  
  if (loading) {
    return (
      <div className="rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] p-6">
        <div className="flex items-center justify-center gap-3 py-8">
          <Loader2 size={24} className="animate-spin text-emerald-500" />
          <span className="text-stone-600 dark:text-[#C7DDD0]">Calculating price...</span>
        </div>
      </div>
    );
  }

  // Use new quote fields
  const platformPriceDollars = quote?.platform_price_cents ? quote.platform_price_cents / 100 : quote?.total_cost || 0;
  const creditsRequired = quote?.credits_required || quote?.total_credits || Math.ceil(platformPriceDollars * 100);
  const availableCredits = quote?.available_credits || 0;
  const pageCount = quote?.total_pages || quote?.page_count || envelopes?.reduce((sum, e) => sum + (e.page_count || 2), 0) || 0;
  const envelopeCount = quote?.envelopes || envelopes?.length || 0;
  const costSource = quote?.cost_source || 'configured_rate_table';
  const canSend = quote?.can_send !== false && quote?.can_afford !== false;

  return (
    <div className="space-y-4">
      {/* Mail Class Selection - Updated with 3 options */}
      <div className="rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] p-5">
        <h4 className="font-semibold text-stone-800 dark:text-[#EAF7F0] mb-4 flex items-center gap-2">
          <Mail size={18} className="text-emerald-600" />
          Select Mail Class
        </h4>
        <div className="space-y-3">
          {mailClasses.map((mc) => {
            const IconComponent = mc.icon || Mail;
            const isSelected = mailClass === mc.value;
            
            return (
              <button
                key={mc.value}
                onClick={() => onMailClassChange(mc.value)}
                data-testid={`mail-class-${mc.value}`}
                className={`w-full p-4 rounded-lg border text-left transition-all ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-[rgba(16,185,129,0.15)] ring-2 ring-emerald-500/30'
                    : 'border-stone-200 dark:border-[rgba(142,218,182,0.20)] hover:border-emerald-300 dark:hover:border-emerald-600/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-stone-100 dark:bg-stone-800/50'}`}>
                    <IconComponent size={20} className={isSelected ? 'text-emerald-600' : 'text-stone-500 dark:text-stone-400'} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${isSelected ? 'text-emerald-700 dark:text-emerald-400' : 'text-stone-800 dark:text-[#EAF7F0]'}`}>
                        {mc.label}
                      </span>
                      {isSelected && <Check size={18} className="text-emerald-600" />}
                    </div>
                    <p className="text-sm text-stone-500 dark:text-[#93AE9F] mt-0.5">{mc.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-stone-400 dark:text-[#6B8F7A]">
                      {/* Don't show assumed delivery days - only show when PostGrid provides actual estimate */}
                      {mc.includesTracking && (
                        <Badge variant="outline" className="text-xs py-0 px-1.5 border-blue-300 text-blue-600">
                          Tracking
                        </Badge>
                      )}
                      {mc.includesSignature && (
                        <Badge variant="outline" className="text-xs py-0 px-1.5 border-purple-300 text-purple-600">
                          Signature
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Cost Summary */}
      <div className="rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] p-5">
        <h4 className="font-semibold text-stone-800 dark:text-[#EAF7F0] mb-4 flex items-center gap-2">
          <DollarSign size={18} className="text-emerald-600" />
          Cost Summary
        </h4>
        
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-stone-600 dark:text-[#C7DDD0]">Envelopes</span>
            <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">{envelopeCount}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-stone-600 dark:text-[#C7DDD0]">Total Pages (est.)</span>
            <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">{pageCount}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-stone-600 dark:text-[#C7DDD0]">Mail Class</span>
            <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">
              {mailClasses.find(mc => mc.value === mailClass)?.label || mailClass}
            </span>
          </div>
          
          <div className="border-t border-stone-100 dark:border-[rgba(142,218,182,0.10)] pt-3 mt-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <DollarSign size={18} className="text-emerald-600" />
                <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">Total Cost</span>
              </div>
              <span className="text-xl font-bold text-emerald-600" data-testid="total-cost">
                ${platformPriceDollars.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <div className="flex items-center gap-2">
                <CreditCard size={18} className="text-blue-600" />
                <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">Credits Required</span>
              </div>
              <span className="text-xl font-bold text-blue-600" data-testid="credits-required">
                {creditsRequired.toLocaleString()} credits
              </span>
            </div>
            <div className="flex justify-between items-center mt-2 text-sm">
              <span className="text-stone-500 dark:text-[#93AE9F]">Available Credits</span>
              <span className={`font-medium ${availableCredits >= creditsRequired ? 'text-emerald-600' : 'text-red-500'}`} data-testid="available-credits">
                {availableCredits.toLocaleString()} credits
              </span>
            </div>
          </div>
          
          {/* Cost Source Indicator */}
          <div className="flex items-center gap-2 text-xs text-stone-400 dark:text-[#6B8F7A] mt-2">
            <Info size={12} />
            <span>
              {costSource === 'postgrid_quote' 
                ? 'Price from PostGrid quote' 
                : costSource === 'postgrid_actual'
                ? 'Actual PostGrid cost'
                : 'Estimated from rate table'}
            </span>
          </div>
        </div>

        {!canSend && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-[rgba(239,68,68,0.10)] border border-red-200 dark:border-[rgba(239,68,68,0.20)]">
            <p className="text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
              <AlertCircle size={16} />
              Insufficient credits. Required: {creditsRequired.toLocaleString()}. Available: {availableCredits.toLocaleString()}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT: DeliveryFlowStep8
// ============================================
const DeliveryFlowStep8 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, getAuthHeaders } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [letters, setLetters] = useState([]);
  const [envelopes, setEnvelopes] = useState([]);
  const [senderAddresses, setSenderAddresses] = useState({});
  const [packageId, setPackageId] = useState(null);
  const [packageIdentitySupport, setPackageIdentitySupport] = useState({});
  const [perLetterAttachments, setPerLetterAttachments] = useState({});
  const [packageSharedAttachments, setPackageSharedAttachments] = useState([]);
  const [dataBreachProof, setDataBreachProof] = useState(null);
  const [ssnApprovals, setSsnApprovals] = useState({});
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [mailClass, setMailClass] = useState('standard');  // New default: 'standard' (was 'first_class')
  const [sending, setSending] = useState(false);
  const [validationError, setValidationError] = useState(null);  // Generic message
  const [validationErrors, setValidationErrors] = useState([]);  // Structured errors array [{code, field, message}]
  const [creditBalance, setCreditBalance] = useState(0);
  const [addingCredits, setAddingCredits] = useState(false);
  const [insufficientCredits, setInsufficientCredits] = useState(false);

  // ============================================
  // INITIALIZE
  // ============================================
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get data from previous step state
        const stepData = location.state || {};
        const letterPackage = stepData.letterPackage || stepData.letters;
        const envs = stepData.envelopes || [];
        const addrs = stepData.senderAddresses || {};
        const pkgId = stepData.packageId || stepData.package_id;
        const identitySupport = stepData.packageIdentitySupport || {};
        const letterAttachments = stepData.perLetterAttachments || {};
        const sharedAttachments = stepData.packageSharedAttachments || [];
        const breachProof = stepData.dataBreachProof;
        const ssnApprovalState = stepData.ssnApprovals || {};

        if (!letterPackage || envs.length === 0) {
          throw new Error('No envelope data provided. Please complete previous steps first.');
        }

        const {
          envelopes: dedupedEnvs,
          removedCount: duplicateEnvelopesRemoved,
        } = dedupeDataBreachEnvelopes(envs, { packageId: pkgId });
        if (duplicateEnvelopesRemoved > 0) {
          console.warn('[STEP 8] Removed duplicate Data Breach envelopes on load', {
            duplicateEnvelopesRemoved,
            before: envs.length,
            after: dedupedEnvs.length,
          });
        }

        // Set state
        const lettersArray = letterPackage.letters || letterPackage;
        setLetters(Array.isArray(lettersArray) ? lettersArray : [lettersArray]);
        setEnvelopes(dedupedEnvs);
        setSenderAddresses(addrs);
        setPackageId(pkgId);
        setPackageIdentitySupport(identitySupport);
        setPerLetterAttachments(letterAttachments);
        setPackageSharedAttachments(sharedAttachments);
        setDataBreachProof(breachProof);
        setSsnApprovals(ssnApprovalState);

        setLoading(false);
      } catch (err) {
        console.error('Init error:', err);
        // Ensure error is always a string
        const errorMsg = typeof err.message === 'string' ? err.message : 
          (typeof err === 'string' ? err : JSON.stringify(err));
        setError(errorMsg || 'Failed to load review');
        setLoading(false);
      }
    };

    init();
  }, [location]);

  useEffect(() => {
    const resolveBreachProof = async () => {
      const hasDataBreachEnvelope = envelopes.some((env) => isDataBreachEnvelope(env));
      if (!hasDataBreachEnvelope || isResolvableDataBreachProof(dataBreachProof)) return;

      try {
        const response = await axios.get(`${API}/package/data-breach-proof`, {
          headers: getAuthHeaders(),
          withCredentials: true,
        });
        if (response.data?.success && response.data?.proof) {
          setDataBreachProof(normalizeDataBreachProof(response.data.proof));
          return;
        }
      } catch {
        // fall through to system default
      }
      setDataBreachProof(buildSystemDefaultDataBreachProof());
    };

    if (!loading && envelopes.length > 0) {
      resolveBreachProof();
    }
  }, [loading, envelopes, dataBreachProof, getAuthHeaders]);

  // Fetch credit balance on mount
  useEffect(() => {
    fetchCreditBalance();
  }, []);

  // Fetch quote when envelopes or mail class changes
  useEffect(() => {
    if (envelopes.length > 0 && !loading) {
      fetchQuote();
    }
  }, [envelopes, mailClass, loading]);

  // Fetch user's credit balance
  const fetchCreditBalance = async () => {
    try {
      const response = await axios.get(
        `${API}/delivery/credits/balance`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      if (response.data) {
        setCreditBalance(response.data.available_credits || 0);
      }
    } catch (err) {
      console.warn('Failed to fetch credit balance:', err);
      setCreditBalance(0);
    }
  };

  // Add test credits
  const handleAddTestCredits = async () => {
    setAddingCredits(true);
    try {
      const response = await axios.post(
        `${API}/delivery/credits/add-test-credits`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      if (response.data?.success) {
        toast.success(`Added ${response.data.new_balance - creditBalance} test credits!`);
        setCreditBalance(response.data.new_balance);
        setInsufficientCredits(false);
        setValidationError(null);
        setValidationErrors([]);
        // Refresh quote
        if (envelopes.length > 0) {
          fetchQuote();
        }
      }
    } catch (err) {
      console.error('Failed to add test credits:', err);
      toast.error('Failed to add test credits');
    }
    setAddingCredits(false);
  };

  const fetchQuote = async () => {
    setQuoteLoading(true);
    try {
      // First, try the new package-quote endpoint (simplified, uses backend calculations)
      if (packageId) {
        try {
          const packageQuoteResponse = await axios.post(
            `${API}/delivery/mail/package-quote`,
            {
              package_id: packageId,
              mail_class: mailClass
            },
            { headers: getAuthHeaders(), withCredentials: true }
          );
          
          if (packageQuoteResponse.data?.ok) {
            const pq = packageQuoteResponse.data;
            setQuote({
              quote_id: pq.quote_id,
              total_cost: pq.platform_price_cents / 100,
              platform_price_cents: pq.platform_price_cents,
              total_credits: pq.credits_required,
              credits_required: pq.credits_required,
              total_pages: pq.total_pages,
              envelopes: pq.envelopes,
              page_count: pq.total_pages,
              can_afford: pq.can_send,
              can_send: pq.can_send,
              available_credits: pq.available_credits,
              mail_class: pq.mail_class,
              mail_class_label: pq.label,
              cost_source: pq.cost_source,
              price_breakdown: pq.price_breakdown,
              warnings: pq.warnings || []
            });
            
            // Update credit balance from quote response
            if (pq.available_credits !== undefined) {
              setCreditBalance(pq.available_credits);
            }
            
            // Check if insufficient credits
            setInsufficientCredits(!pq.can_send);
            setQuoteLoading(false);
            return;
          }
        } catch (pkgErr) {
          console.log('Package quote not available, falling back to envelope quote:', pkgErr.message);
        }
      }
      
      // Fallback to envelope-quote endpoint
      // Build envelope payload for quote
      const { envelopes: quoteEnvelopes } = dedupeDataBreachEnvelopes(envelopes, { packageId });
      const envelopePayloads = quoteEnvelopes.map((env, idx) => {
        const sender = senderAddresses[env.envelope_id] || {};
        // Get letter ID from multiple sources (fallback chain)
        const letterId = env.letter_id || env.letters?.[0]?.id || env.letters?.[0]?.letter_id || 
                         env.envelope_id || `letter_${env.bureau?.toLowerCase()}_${idx}`;
        
        const attachments = buildMailEnvelopeAttachments({
          envelope: env,
          packageIdentitySupport,
          dataBreachProof,
          perLetterAttachments,
          packageSharedAttachments,
          ssnApprovals,
        });

        return {
          envelope_id: env.envelope_id || `env_${letterId}`,
          letter_id: letterId,
          bureau: env.bureau,
          method: env.method,
          envelope_type: env.envelope_type,
          sender_address: {
            name: sender.full_name || sender.name || 'User',
            full_name: sender.full_name || sender.name || 'User',
            address_line_1: sender.address_line_1 || sender.street || '',
            address_line_2: sender.address_line_2 || '',
            city: sender.city || '',
            state: sender.state || '',
            postal_code: sender.postal_code || sender.zip || '',
            zip: sender.postal_code || sender.zip || '',
            country: 'US'
          },
          recipient_address: env.recipient_address,
          attachments,
          mail_class: mailClass
        };
      });

      const response = await axios.post(
        `${API}/delivery/mail/envelope-quote`,
        {
          package_id: packageId,
          envelopes: envelopePayloads,
          mail_class: mailClass
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );

      if (response.data) {
        setQuote({
          quote_id: response.data.quote_id,
          total_cost: response.data.total_price_dollars,
          platform_price_cents: response.data.total_price_cents,
          total_credits: response.data.credits_required,
          credits_required: response.data.credits_required,
          page_count: response.data.total_pages,
          total_pages: response.data.total_pages,
          envelopes: response.data.envelope_count,
          can_afford: response.data.can_afford,
          can_send: response.data.can_afford,
          available_credits: response.data.available_credits,
          mail_class: response.data.mail_class,
          cost_source: 'configured_rate_table'
        });
        // Update credit balance from quote response if available
        if (response.data.available_credits !== undefined) {
          setCreditBalance(response.data.available_credits);
        }
        // Check if insufficient credits
        if (!response.data.can_afford) {
          setInsufficientCredits(true);
        } else {
          setInsufficientCredits(false);
        }
      }
    } catch (err) {
      console.error('Quote error:', err);
      setQuote(null);
      toast.error('Unable to calculate mail quote. Please try again.');
    }
    setQuoteLoading(false);;
  };

  // Build final payload for PostGrid
  const buildFinalPayload = () => {
    const { envelopes: sendEnvelopes, removedCount: duplicateEnvelopesRemoved } =
      dedupeDataBreachEnvelopes(envelopes, { packageId });
    if (duplicateEnvelopesRemoved > 0) {
      console.warn('[STEP 8] Removed duplicate Data Breach envelopes before send', {
        duplicateEnvelopesRemoved,
        before: envelopes.length,
        after: sendEnvelopes.length,
      });
    }

    const payloads = sendEnvelopes.map((env, idx) => {
      const sender = senderAddresses[env.envelope_id] || {};
      // Get letter ID from multiple sources (fallback chain)
      const letterId = env.letter_id || env.letters?.[0]?.id || env.letters?.[0]?.letter_id || 
                       env.package_id || env.envelope_id || `letter_${env.bureau?.toLowerCase()}_${idx}`;
      
      console.log(`[STEP8_ENVELOPE_${idx}]`, { 
        letter_id: letterId, 
        envelope_id: env.envelope_id, 
        bureau: env.bureau,
        has_sender: !!sender.full_name || !!sender.name
      });
      
      const attachments = buildMailEnvelopeAttachments({
        envelope: { ...env, letter_id: letterId },
        packageIdentitySupport,
        dataBreachProof,
        perLetterAttachments,
        packageSharedAttachments,
        ssnApprovals,
      });

      return {
        envelope_id: env.envelope_id,
        letter_id: letterId,
        bureau: env.bureau,
        method: env.method,
        envelope_type: env.envelope_type,
        sender_address: {
          // Send both 'name' and 'full_name' for backend compatibility
          name: sender.full_name || sender.name || '',
          full_name: sender.full_name || sender.name || '',
          address_line_1: sender.address_line_1 || sender.street || '',
          address_line_2: sender.address_line_2 || '',
          city: sender.city || '',
          state: sender.state || '',
          postal_code: sender.postal_code || sender.zip || '',
          zip: sender.postal_code || sender.zip || '',
          country: 'US'
        },
        recipient_address: env.recipient_address,
        attachments,
        mail_class: mailClass
      };
    });
    
    return {
      package_id: packageId,
      envelopes: payloads,
      mail_class: mailClass,
      total_cost: quote?.total_cost,
      total_credits: quote?.total_credits,
      duplicate_envelopes_removed: duplicateEnvelopesRemoved,
    };
  };

  // State for post-send view
  const [sendComplete, setSendComplete] = useState(false);
  const [deliveryId, setDeliveryId] = useState(null);
  
  // ============================================
  // DOWNLOAD PRINTABLE PDF HANDLER
  // ============================================
  const handleDownloadPrintable = async (envelopeIdx = 0) => {
    try {
      const envelope = envelopes[envelopeIdx];
      if (!envelope) {
        toast.error('Envelope not found');
        return;
      }
      
      const sender = senderAddresses[envelope.envelope_id] || {};
      
      toast.loading('Generating printable PDF...', { id: 'download-pdf' });
      
      const pdfFilename = buildDisputeLetterPdfFilename({
        bureau: envelope.bureau,
        method_used: envelope.method,
        package_method: envelope.method,
      });
      
      const response = await axios.post(
        `${API}/delivery/pdf/generate-printable`,
        {
          package_id: packageId,
          envelope_id: envelope.envelope_id,
          filename: pdfFilename,
          sender_address: {
            full_name: sender.full_name || '',
            address_line_1: sender.address_line_1 || '',
            address_line_2: sender.address_line_2 || '',
            city: sender.city || '',
            state: sender.state || '',
            postal_code: sender.postal_code || sender.zip || '',
          },
          recipient_address: envelope.recipient_address,
          include_attachments: true
        },
        {
          headers: getAuthHeaders(),
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Download the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = pdfFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Printable PDF downloaded!', { id: 'download-pdf' });
    } catch (err) {
      console.error('Download error:', err);
      toast.error(err.response?.data?.detail || 'Failed to download PDF', { id: 'download-pdf' });
    }
  };
  
  // Send to PostGrid with PostGrid PDF validation
  const handleSend = async () => {
    if (!quote || quote.can_afford === false) {
      toast.error('Please ensure you have sufficient credits');
      return;
    }

    setSending(true);
    setValidationError(null);  // Clear previous validation errors
    setValidationErrors([]);   // Clear previous structured errors
    
    try {
      // First check PostGrid health/connection
      try {
        const healthRes = await axios.get(
          `${API}/delivery/postgrid/health`,
          { headers: getAuthHeaders(), withCredentials: true }
        );
        
        if (!healthRes.data?.connected) {
          setValidationErrors([{
            code: 'postgrid_not_connected',
            field: 'postgrid_integration',
            message: healthRes.data?.errors?.[0] || 'PostGrid is not connected. Please configure PostGrid in Admin Settings before mailing.'
          }]);
          setValidationError('PostGrid is not connected.');
          toast.error('PostGrid integration not configured');
          setSending(false);
          return;
        }
      } catch (healthErr) {
        console.warn('PostGrid health check failed:', healthErr);
        // Continue anyway - the send endpoint will also check
      }
      
      const payload = buildFinalPayload();

      await syncWizardPackagePacketAttachments({
        letters: payload.envelopes.map((env) => ({
          ...env,
          id: env.letter_id,
          letter_id: env.letter_id,
          bureau: env.bureau,
          method_used: env.method,
          envelope_type: env.envelope_type,
        })),
        packageIdentitySupport,
        dataBreachProof,
        perLetterAttachments,
        packageSharedAttachments,
        ssnApprovals,
        packageId,
        includedInPostgrid: true,
        metro2Service,
        token,
      });
      
      // Log payload keys for debugging (not sensitive data)
      console.log('[STEP8_SEND_PAYLOAD_KEYS]', {
        package_id: payload.package_id,
        envelopes_count: payload.envelopes?.length,
        mail_class: payload.mail_class,
        has_total_cost: !!payload.total_cost,
        has_total_credits: !!payload.total_credits
      });
      
      const response = await axios.post(
        `${API}/delivery/mail/envelope-send`,
        payload,
        { headers: getAuthHeaders(), withCredentials: true }
      );

      if (response.data?.success) {
        toast.success('Package sent successfully!');
        setDeliveryId(response.data.delivery_id);
        setSendComplete(true);
        // Stay in wizard - don't redirect to My Disputes automatically
      } else {
        // Check for insufficient credits
        if (response.data?.code === 'insufficient_credits') {
          setInsufficientCredits(true);
          setCreditBalance(response.data.available_credits || 0);
          const structuredErrors = response.data?.validation_errors || [];
          setValidationErrors(structuredErrors);
          setValidationError(response.data?.error || "Insufficient credits to send this package.");
          toast.error('Insufficient credits');
        } else {
          // Check for structured validation errors
          const rawErrors = response.data?.validation_errors || response.data?.errors || [];
          // Normalize error objects to have a consistent 'message' field
          const structuredErrors = rawErrors.map(e => {
            // Handle PostGrid error format: {envelope_id, error}
            if (e.envelope_id && e.error) {
              return {
                code: 'postgrid_error',
                field: e.envelope_id,
                message: `Envelope ${e.envelope_id}: ${e.error}`
              };
            }
            // Handle standard format: {code, field, message}
            if (e.message) return e;
            // Handle string errors
            if (typeof e === 'string') return { code: 'error', message: e };
            // Fallback
            return { code: 'error', message: JSON.stringify(e) };
          });
          if (structuredErrors && structuredErrors.length > 0) {
            // Check if any are credit errors
            const hasCreditError = structuredErrors.some(e => e.code === 'insufficient_credits');
            if (hasCreditError) {
              setInsufficientCredits(true);
              const creditErr = structuredErrors.find(e => e.code === 'insufficient_credits');
              setCreditBalance(creditErr?.available_credits || 0);
            }
            // Store structured errors for detailed display
            setValidationErrors(structuredErrors);
            setValidationError(response.data?.error || "Package validation failed. Please review the errors below.");
            toast.error('Package validation failed');
          } else {
            throw new Error(response.data?.error || response.data?.message || 'Failed to send');
          }
        }
      }
    } catch (err) {
      console.error('Send error:', err);
      const errorData = err.response?.data;
      const rawErrors = errorData?.validation_errors || errorData?.errors || [];
      
      // Normalize error objects to have a consistent 'message' field
      const normalizeErrors = (errors) => errors.map(e => {
        // Handle PostGrid error format: {envelope_id, error}
        if (e.envelope_id && e.error) {
          return {
            code: 'postgrid_error',
            field: e.envelope_id,
            message: `Envelope ${e.envelope_id}: ${e.error}`
          };
        }
        // Handle standard format: {code, field, message}
        if (e.message) return e;
        // Handle string errors
        if (typeof e === 'string') return { code: 'error', message: e };
        // Fallback
        return { code: 'error', message: JSON.stringify(e) };
      });
      
      const structuredErrors = normalizeErrors(rawErrors);
      
      // Check for insufficient credits response
      if (errorData?.code === 'insufficient_credits') {
        setInsufficientCredits(true);
        setCreditBalance(errorData.available_credits || 0);
        const creditErrors = normalizeErrors(errorData?.validation_errors || []);
        setValidationErrors(creditErrors);
        setValidationError(errorData?.error || "Insufficient credits to send this package.");
        toast.error('Insufficient credits');
      } else if (structuredErrors && structuredErrors.length > 0) {
        // Check if any are credit errors
        const hasCreditError = structuredErrors.some(e => e.code === 'insufficient_credits');
        if (hasCreditError) {
          setInsufficientCredits(true);
          const creditErr = structuredErrors.find(e => e.code === 'insufficient_credits');
          setCreditBalance(creditErr?.available_credits || 0);
        }
        // Store structured errors for detailed display
        setValidationErrors(structuredErrors);
        setValidationError(errorData?.error || "Package validation failed. Please review the errors below.");
        toast.error('Package validation failed');
      } else {
        // Handle other errors - parse Pydantic validation errors
        const detail = errorData?.detail;
        let errorMsg = 'Failed to send package';
        
        if (typeof detail === 'string') {
          // Check if it's a technical error we should hide
          if (detail.toLowerCase().includes('overlapping') || 
              detail.toLowerCase().includes('address region')) {
            errorMsg = 'Mailing PDF failed address-window validation.';
            setValidationErrors([{
              code: 'address_window_overlap',
              field: 'postgrid_pdf_url',
              message: 'Content overlaps the address window area. Please regenerate the letter.'
            }]);
          } else if (detail.toLowerCase().includes('validation')) {
            errorMsg = 'Package validation failed';
          } else {
            errorMsg = detail;
          }
          setValidationError(errorMsg);
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors - map to user-friendly messages
          const errors = detail.map(d => {
            const fieldPath = d.loc?.slice(1).join('.') || 'unknown';  // Skip first element (body)
            let message = d.msg || String(d);
            
            // Map generic "Field required" to specific field names
            if (message === 'Field required') {
              const fieldName = fieldPath.split('.').pop();
              const fieldLabels = {
                'name': 'Name',
                'full_name': 'Full name',
                'address_line_1': 'Street address',
                'city': 'City',
                'state': 'State',
                'postal_code': 'ZIP code',
                'zip': 'ZIP code',
                'letter_id': 'Letter ID',
                'envelope_id': 'Envelope ID',
                'bureau': 'Bureau'
              };
              const label = fieldLabels[fieldName] || fieldName;
              message = `${label} is required.`;
            }
            
            return {
              code: 'validation_error',
              field: fieldPath,
              message: message
            };
          });
          setValidationErrors(errors);
          setValidationError('Package validation failed. Please review the errors below.');
        } else if (detail?.msg) {
          setValidationError(detail.msg);
        } else if (errorData?.error) {
          setValidationError(errorData.error);
        } else if (err.message && !err.message.includes('500')) {
          setValidationError(err.message);
        } else {
          setValidationError('Failed to send package. Please try again.');
        }
        toast.error(errorMsg);
      }
    }
    setSending(false);
  };

  // Navigation
  const handleBack = () => {
    navigate('/dispute-wizard/step7-envelope', {
      state: location.state
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9F8] dark:bg-[#0a1f17] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-emerald-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 dark:text-[#EAF7F0] mb-2">Loading Review</h3>
          <p className="text-stone-600 dark:text-[#C7DDD0]">Preparing your final review...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    // Ensure error is always a string for React rendering
    const errorMessage = typeof error === 'string' 
      ? error 
      : (error?.message || error?.detail || JSON.stringify(error));
    
    return (
      <div className="min-h-screen bg-[#F7F9F8] dark:bg-[#0a1f17] flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 dark:text-[#EAF7F0] mb-2">Error</h3>
          <p className="text-stone-600 dark:text-[#C7DDD0] mb-4">{errorMessage}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft size={16} className="mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Success state - send complete
  if (sendComplete) {
    return (
      <div className="min-h-screen bg-[#F7F9F8] dark:bg-[#0a1f17]" data-testid="delivery-flow-step8-complete">
        {/* Full 8-Step Wizard Stepper - All complete */}
        <DisputeWizardStepper currentStep={7} compact={true} />
        
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] flex items-center justify-center">
            <CheckCircle size={40} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 dark:text-[#EAF7F0] mb-3">
            Package Sent Successfully!
          </h1>
          <p className="text-stone-600 dark:text-[#C7DDD0] mb-8">
            Your dispute letters have been sent to PostGrid for printing and mailing.
            You'll receive tracking information once they're dispatched.
          </p>
          {deliveryId && (
            <p className="text-sm text-stone-500 dark:text-[#93AE9F] mb-8">
              Delivery ID: {deliveryId}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dispute-wizard')}
              className="dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]"
            >
              Start New Dispute
            </Button>
            <Button 
              onClick={() => navigate('/my-disputes', { state: { sent: true, deliveryId } })}
              className="text-white bg-emerald-600 hover:bg-emerald-700"
            >
              View in My Disputes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F9F8] dark:bg-[#0a1f17]" data-testid="delivery-flow-step8">
      {/* Full 8-Step Wizard Stepper */}
      <DisputeWizardStepper currentStep={7} compact={true} />
      
      {/* Header */}
      <div className="bg-white dark:bg-[rgba(10,38,28,0.85)] border-b border-stone-200 dark:border-[rgba(142,218,182,0.15)] sticky top-0 z-10" style={{ top: '52px' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <Button variant="ghost" onClick={handleBack} className="dark:text-[#C7DDD0] text-sm" data-testid="back-btn">
              <ArrowLeft size={16} className="mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Back to Step 6</span>
              <span className="xs:hidden">Back</span>
            </Button>
            <div className="hidden sm:block h-6 w-px bg-stone-200 dark:bg-[rgba(142,218,182,0.15)]" />
            <div className="flex items-center gap-2">
              <Send size={18} className="text-emerald-600 flex-shrink-0" />
              <h1 className="text-base sm:text-lg font-bold text-stone-800 dark:text-[#EAF7F0]">
                <span className="hidden sm:inline">Step 7: Send Mail</span>
                <span className="sm:hidden">Send Mail</span>
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <Button 
              onClick={handleSend} 
              disabled={sending || !quote?.can_afford} 
              className="text-white bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto text-sm sm:text-base" 
              data-testid="send-btn"
            >
              {sending ? <Loader2 size={14} className="mr-1 sm:mr-2 animate-spin" /> : <Mail size={14} className="mr-1 sm:mr-2" />}
              Send by Mail
            </Button>
          </div>
        </div>

        {/* Delivery Flow Sub-Stepper (Steps 5-7) */}
        <DeliveryFlowStepper currentStep={7} />
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6 min-w-0 overflow-x-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          {/* Package Summary */}
          <div className="space-y-4">
            <div className="rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] p-5">
              <div className="flex items-center gap-2 mb-4">
                <Package size={20} className="text-emerald-600" />
                <h4 className="font-semibold text-stone-800 dark:text-[#EAF7F0]">Package Summary</h4>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600 dark:text-[#C7DDD0]">Total Envelopes</span>
                  <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">{envelopes.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-600 dark:text-[#C7DDD0]">Total Letters</span>
                  <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">{letters.length}</span>
                </div>
                
                {/* Bureaus */}
                <div className="pt-2 border-t border-stone-100 dark:border-[rgba(142,218,182,0.10)]">
                  <p className="text-sm text-stone-500 dark:text-[#93AE9F] mb-2">Bureaus:</p>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(envelopes.map(e => e.bureau))].map(bureau => (
                      <Badge key={bureau} className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-400">
                        {bureau}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Envelope List */}
            <div className="rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] p-5">
              <h4 className="font-semibold text-stone-800 dark:text-[#EAF7F0] mb-4">Envelopes to Send</h4>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {envelopes.map((env, idx) => {
                  const sender = senderAddresses[env.envelope_id] || {};
                  const recipient = env.recipient_address || {};
                  
                  // Check for missing fields for this envelope
                  const senderMissing = [];
                  if (!sender.full_name) senderMissing.push('name');
                  if (!sender.address_line_1) senderMissing.push('street');
                  if (!sender.city) senderMissing.push('city');
                  if (!sender.state) senderMissing.push('state');
                  if (!sender.postal_code && !sender.zip) senderMissing.push('ZIP');
                  
                  const recipientMissing = [];
                  if (!recipient.name) recipientMissing.push('name');
                  if (!recipient.address_line_1) recipientMissing.push('street');
                  if (!recipient.city) recipientMissing.push('city');
                  if (!recipient.state) recipientMissing.push('state');
                  if (!recipient.postal_code) recipientMissing.push('ZIP');
                  
                  const hasWarning = senderMissing.length > 0 || recipientMissing.length > 0;
                  
                  return (
                    <div 
                      key={env.envelope_id} 
                      className={`p-3 rounded-lg ${hasWarning ? 'bg-amber-50 dark:bg-[rgba(245,158,11,0.10)] border border-amber-200 dark:border-[rgba(245,158,11,0.20)]' : 'bg-stone-50 dark:bg-[rgba(10,38,28,0.40)]'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">
                          Envelope {idx + 1}: {env.bureau}
                        </span>
                        <div className="flex items-center gap-2">
                          {hasWarning && (
                            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-300">
                              <AlertCircle size={10} className="mr-1" />
                              Incomplete
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">
                            {env.method?.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Full Return Address */}
                      <div className="mb-2">
                        <p className="text-xs font-medium text-stone-600 dark:text-[#93AE9F] mb-1">From:</p>
                        <div className="text-sm text-stone-700 dark:text-[#C7DDD0] pl-2 border-l-2 border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                          <p className="font-medium">{sender.full_name || <span className="text-amber-600">Name missing</span>}</p>
                          <p>{sender.address_line_1 || <span className="text-amber-600">Street address missing</span>}</p>
                          {sender.address_line_2 && <p>{sender.address_line_2}</p>}
                          <p>
                            {sender.city || <span className="text-amber-600">City missing</span>}, {' '}
                            {sender.state || <span className="text-amber-600">State missing</span>} {' '}
                            {sender.postal_code || sender.zip || <span className="text-amber-600">ZIP missing</span>}
                          </p>
                        </div>
                      </div>
                      
                      {/* Full Recipient Address */}
                      <div>
                        <p className="text-xs font-medium text-stone-600 dark:text-[#93AE9F] mb-1">To:</p>
                        <div className="text-sm text-stone-700 dark:text-[#C7DDD0] pl-2 border-l-2 border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
                          <p className="font-medium">{recipient.name || <span className="text-amber-600">Name missing</span>}</p>
                          <p>{recipient.address_line_1 || <span className="text-amber-600">Street address missing</span>}</p>
                          {recipient.address_line_2 && <p>{recipient.address_line_2}</p>}
                          <p>
                            {recipient.city || <span className="text-amber-600">City missing</span>}, {' '}
                            {recipient.state || <span className="text-amber-600">State missing</span>} {' '}
                            {recipient.postal_code || <span className="text-amber-600">ZIP missing</span>}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Attachments Summary */}
            <div className="rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] p-5">
              <h4 className="font-semibold text-stone-800 dark:text-[#EAF7F0] mb-4">Documents Included</h4>
              <div className="space-y-2">
                {packageIdentitySupport?.proof_of_address && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle size={14} className="text-emerald-500" />
                    <span className="text-stone-700 dark:text-[#C7DDD0]">Proof of Address (all envelopes)</span>
                  </div>
                )}
                {packageIdentitySupport?.government_id && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle size={14} className="text-emerald-500" />
                    <span className="text-stone-700 dark:text-[#C7DDD0]">Government ID (all envelopes)</span>
                  </div>
                )}
                {dataBreachProof && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle size={14} className="text-amber-500" />
                    <span className="text-stone-700 dark:text-[#C7DDD0]">Data Breach Proof (data breach envelopes only)</span>
                  </div>
                )}
                {(!packageIdentitySupport?.proof_of_address && !packageIdentitySupport?.government_id && !dataBreachProof) && (
                  <p className="text-sm text-stone-500 dark:text-[#93AE9F]">No supporting documents attached</p>
                )}
              </div>
            </div>
            
            {/* Mailing Settings - Default: B&W, Single-sided, Separate Address Page */}
            <div className="rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] p-5">
              <h4 className="font-semibold text-stone-800 dark:text-[#EAF7F0] mb-4">Mailing Settings</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-stone-600 dark:text-[#C7DDD0]">Print Color</span>
                  <Badge className="bg-stone-100 dark:bg-[rgba(142,218,182,0.15)] text-stone-700 dark:text-[#C7DDD0]">
                    Black & White
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-600 dark:text-[#C7DDD0]">Print Sides</span>
                  <Badge className="bg-stone-100 dark:bg-[rgba(142,218,182,0.15)] text-stone-700 dark:text-[#C7DDD0]">
                    Single-sided
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-600 dark:text-[#C7DDD0]">Envelope Type</span>
                  <Badge className="bg-stone-100 dark:bg-[rgba(142,218,182,0.15)] text-stone-700 dark:text-[#C7DDD0]">
                    #10 Double Window
                  </Badge>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-stone-100 dark:border-[rgba(142,218,182,0.10)]">
                  <span className="text-stone-600 dark:text-[#C7DDD0]">Address Page</span>
                  <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-400">
                    Separate PostGrid Page
                  </Badge>
                </div>
                <p className="text-xs text-stone-500 dark:text-[#93AE9F] pt-1">
                  Recommended. PostGrid adds a separate address page to prevent content overlap. Adds {envelopes.length} address page{envelopes.length !== 1 ? 's' : ''}.
                </p>
              </div>
            </div>
          </div>

          {/* Price & Credits */}
          <div>
            <PriceCreditsReview
              quote={quote}
              loading={quoteLoading}
              mailClass={mailClass}
              onMailClassChange={setMailClass}
              envelopes={envelopes}
            />

            {/* Credit Balance Section */}
            <div className="mt-4 rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.15)] bg-white dark:bg-[rgba(10,38,28,0.50)] p-5">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard size={18} className="text-emerald-600" />
                <h4 className="font-semibold text-stone-800 dark:text-[#EAF7F0]">Credit Balance</h4>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-stone-600 dark:text-[#C7DDD0]">Required Credits</span>
                  <span className="font-medium text-stone-800 dark:text-[#EAF7F0]">
                    {quote?.total_credits || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-600 dark:text-[#C7DDD0]">Available Credits</span>
                  <span className={`font-medium ${creditBalance < (quote?.total_credits || 0) ? 'text-red-600' : 'text-emerald-600'}`}>
                    {creditBalance}
                  </span>
                </div>
                {creditBalance < (quote?.total_credits || 0) && (
                  <div className="flex items-center justify-between pt-1 border-t border-stone-100 dark:border-[rgba(142,218,182,0.10)]">
                    <span className="text-red-600">Shortfall</span>
                    <span className="font-medium text-red-600">
                      {(quote?.total_credits || 0) - creditBalance}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Add Test Credits Button */}
              {creditBalance < (quote?.total_credits || 0) && (
                <div className="mt-4 pt-3 border-t border-stone-100 dark:border-[rgba(142,218,182,0.10)]">
                  <Button 
                    variant="outline"
                    onClick={handleAddTestCredits}
                    disabled={addingCredits}
                    className="w-full bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                    data-testid="add-test-credits-btn"
                  >
                    {addingCredits ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <CreditCard size={16} className="mr-2" />
                    )}
                    Add Test Credits (10,000)
                  </Button>
                  <p className="text-xs text-stone-500 dark:text-[#93AE9F] mt-2 text-center">
                    For testing purposes only
                  </p>
                </div>
              )}
            </div>

            {/* Validation Error Message - Show structured errors as checklist */}
            {(validationError || validationErrors.length > 0) && (
              <div className="mt-4 rounded-xl border border-amber-300 dark:border-[rgba(245,158,11,0.30)] bg-amber-50 dark:bg-[rgba(245,158,11,0.08)] p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 dark:text-amber-300">
                      {validationErrors.length > 0 ? 'Mailing Validation Failed' : 'Mailing Issue Detected'}
                    </p>
                    
                    {/* Show structured errors as checklist */}
                    {validationErrors.length > 0 ? (
                      <ul className="mt-2 space-y-1">
                        {validationErrors.map((err, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400">
                            <span className="text-amber-500 mt-0.5">•</span>
                            <span>{err.message || err}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                        {validationError}
                      </p>
                    )}
                    
                    <div className="mt-3 pt-3 border-t border-amber-200 dark:border-[rgba(245,158,11,0.20)]">
                      <p className="text-sm text-amber-600 dark:text-amber-500">
                        Your download is still available. Fix the issues above or contact support.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Final Confirmation - Only show if no validation errors */}
            {!validationError && validationErrors.length === 0 && (
              <div className="mt-4 rounded-xl border border-emerald-200 dark:border-[rgba(16,185,129,0.20)] bg-emerald-50 dark:bg-[rgba(16,185,129,0.08)] p-5">
                <div className="flex items-start gap-3">
                  <CheckCircle size={20} className="text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800 dark:text-emerald-300">
                      Ready to Send
                    </p>
                    <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">
                      Your dispute letters will be printed and mailed to each credit bureau. 
                      You'll receive tracking information once sent.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryFlowStep8;
